<template>
  <modal class="color-range-selector" v-model="show" size="lg" :header="false">
    <div class="text-center">
      <h3>Configuration des seuils de couleurs</h3>
      <hr>
    </div>
    <div class="row">
      <div class="col-xs-12" v-if="hasPresets">
        <div class="text-center">
          <btn-group>
            <btn v-for="(preset, k) in presets.sc"
                 :key="`preset-${k}`" size="sm"
                 v-model="selectedPreset.key"
                 input-type="radio"
                 :type="preset.key === selectedPreset.key ? 'bactADI' : 'default'"
                 :input-value="preset.key">
              {{ preset.label }}
            </btn>
          </btn-group>
        </div>
        <hr>
      </div>
      <div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
        <table class="table table-responsive">
          <thead>
          <tr>
            <th scope="col">Seuil</th>
            <th scope="col">Couleur</th>
            <th scope="col" v-if="!hasPresetSelected">Actions</th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="(a, b) in tableLines"
                :key="`data-range-${b}`"
                :class="a.bg">
              <td>{{a.value}}</td>
              <td>
                <span class="color-display" :style="`background-color:${a.color.hex8}`">
                </span>
              </td>
              <td v-if="!hasPresetSelected">
                <btn
                    type="info"
                    size="xs"
                    :disabled="a.isSelected"
                    @click="editTableLine(b)">
                    <span class="glyphicon glyphicon-pencil"
                          aria-hidden="true">
                    </span>
                </btn>&nbsp;
                <btn
                    v-if="a.show"
                    type="danger"
                    size="xs"
                    :disabled="a.isSelected"
                    @click="deleteTableLine(b)">
                    <span class="glyphicon glyphicon-remove"
                          aria-hidden="true">
                    </span>
                </btn>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="text-center">
          <hr>
          <sui-checkbox v-model="colorGradient"
                        v-show="!hasPresetSelected"
                        type="reaInfADI"
                        size="sm">
            Gradient entre les couleurs
          </sui-checkbox>
        </div>
      </div>
      <div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
        <div v-if="!hasPresetSelected"
             class="well well-sm no-bottom-margin">
          <h4 class="text-center">Modifier ou ajouter un seuil</h4>
          <table class="table-responsive table">
            <thead>
              <tr>
                <th scope="col">Seuil</th>
                <th scope="col">Couleur</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <cleave class="form-control center-block text-center"
                          v-model="editValue.value"
                          name="cleave-value"
                          autocomplete="off"
                          :disabled="editValue.disabled"
                          :options="cleaveOptions">
                  </cleave>
                </td>
                <td>
                  <span class="color-display" :style="`background-color:${editValue.color.hex8}`">
                  </span>
                </td>
              </tr>
            <tr>
              <td></td>
              <td>
                <btn
                  size="xs"
                  :class="`btn-${addButtonDisplay.style}`"
                  :disabled="addButtonDisplay.disabled"
                  @click="updateTable">
                {{addButtonDisplay.text}} valeur
                </btn>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="col-xs-12 col-sm-6 col-md-4 col-lg-4" v-if="!hasPresetSelected">
        <sketch-picker
            @input="updateColor"
            v-model="currentPickerColor"
            :preset-colors="palette"/>
      </div>
    </div>
    <template slot="footer">
      <btn
          @click="closeModalAndEmitResult"
          :disabled="isEditing">
        Sauvegarder & Quitter
      </btn>
    </template>
  </modal>
</template>
<script>
import assert from '@/sui/tools/assert'
import cleave from 'vue-cleave-component'
import clone from 'clone'
import c from '@/tools/colors/colors'
import suiCheckbox from '@/sui/components/checkbox'
import VueColor from 'vue-color'

const colorBactADI = c.getHexModuleColor('bactADI')

export default {
  name: 'color-range-selector',

  props: {
    value: {
      type: Boolean,
      required: true
    },

    currentConfig: {
      type: Array,
      required: true
    },

    options: {
      type: Object,
      required: true
    },

    presets: {
      type: Object,
      required: false,
      default: function () {
        return null
      }
    }
  },

  data: function () {
    return {
      defaultAvailableColors: c.defaultAvailableColors,
      show: this.value,
      currentPickerColor: colorBactADI,
      cleaveOptions: {
        numeral: true,
        numeralThousandsGroupStyle: 'none',
        numeralIntegerScale: 4,
        numeralDecimalScale: 3
      },
      editValue: {
        value: null,
        color: colorBactADI,
        lineIndex: null,
        disabled: false
      },
      result: null,
      colorGradient: false,
      selectedPreset: {
        key: this.hasPresets ? this.presets.selected : null
      }
    }
  },

  watch: {
    value: function (v) {
      if (v) {
        this.updatePresetSelection()
        this.updateResultAndOptions()
      }
      if (v !== this.show) {
        this.show = v
      }
    },

    show: function (v) {
      this.$emit('input', v)
    },

    'selectedPreset.key': function (v) {
      this.updateResultAndOptions()
    }
  },

  computed: {
    hasPresets: function () {
      const presets = this.presets
      return presets != null &&
              presets.sc != null &&
              presets.sc.length !== 0
    },

    hasPresetSelected: function () {
      return this.hasPresets &&
          this.selectedPreset.key !== 'none'
    },

    displaySelectedPreset: function () {
      if (!this.hasPresets) {
        return ''
      }
      const key = this.selectedPreset.key
      const result = this.presets.sc.find(d => d.key === key)
      assert.notNull(result)

      return result.label
    },

    palette: function () {
      const extendedPalette = c.mapPalettes.extended
      const result = []
      for (let i = 0; i < extendedPalette.length; i++) {
        result.push(extendedPalette[i].hex)
      }
      return result
    },

    isEditing: function () {
      return this.editValue.lineIndex != null
    },

    tableLines: function () {
      const table = this.result
      const result = []
      if (table == null) {
        return result
      }

      const tableLength = table.length
      if (tableLength === 0) {
        return result
      }

      const editLineIndex = this.editValue.lineIndex
      for (let i = 0; i < tableLength; i++) {
        const isSelected = editLineIndex === i
        const t = table[i]
        const v = t.value
        result.push(
          {
            value: v,
            color: t.color,
            show: v !== '0',
            isSelected: isSelected,
            bg: isSelected ? 'bg-light-chronoBioLine' : ''
          }
        )
      }
      return result
    },

    isInvalidEditedValue: function () {
      const { value, lineIndex } = this.editValue
      if (value == null || value === '') {
        return true
      }

      const vFloat = parseFloat(value)
      const currentResult = this.result
      for (let i = 0; i < currentResult.length; i++) {
        if (
          i !== lineIndex &&
          parseFloat(currentResult[i].value) === vFloat
        ) {
          return true
        }
      }
      return false
    },

    addButtonDisplay: function () {
      const isInvalidValue = this.isInvalidEditedValue
      return {
        style: isInvalidValue ? 'temporary' : 'success',
        disabled: isInvalidValue,
        text: this.isEditing ? 'Modifier la' : 'Ajouter une'
      }
    }
  },

  methods: {
    setEditValue: function (value, color, i) {
      this.editValue = {
        value: value,
        color: color,
        lineIndex: i,
        disabled: value === '0'
      }
    },

    updatePresetSelection: function () {
      const presets = this.presets
      if (presets == null) {
        return
      }
      this.selectedPreset.key = presets.selected
    },

    updateResultAndOptions: function () {
      if (this.hasPresetSelected) {
        const selectedPreset = this.presets.sc.find(
          d => d.key === this.selectedPreset.key
        )
        assert.notNull(selectedPreset)
        this.colorGradient = selectedPreset.colorGradient
        this.result = clone(selectedPreset.config)
        return
      }

      this.colorGradient = this.options.colorGradient
      const config = this.currentConfig
      if (config.length === 0) {
        this.result = [ { value: '0', color: colorBactADI } ]
        return
      }
      this.result = config.slice()
    },

    updateColor: function () {
      const currentColor = this.currentPickerColor
      assert.notNull(currentColor, `Picked color can't be null !`)
      this.editValue.color = currentColor
    },

    updateTable: function () {
      const isEditing = this.isEditing
      const editValue = this.editValue

      if (isEditing) {
        this.deleteTableLine(editValue.lineIndex)
      }

      this.addTableLine()
      assert(this.result.length !== 0, `Range table can't be empty !`)
      this.setEditValue(null, editValue.color, null)
    },

    addTableLine: function () {
      const currentResult = this.result
      const editedValue = this.editValue
      if (editedValue == null || currentResult == null) {
        return
      }

      const v = editedValue.value
      const c = editedValue.color

      const newValue = {
        value: v,
        color: c
      }

      const newResult = []
      let add = true

      const vFloat = parseFloat(v)
      for (let i = 0; i < currentResult.length; i++) {
        const currentValue = currentResult[i]
        if (
          add &&
            parseFloat(currentValue.value) > vFloat
        ) {
          add = false
          newResult.push(newValue)
        }
        newResult.push(currentValue)
      }
      if (add) {
        newResult.push(newValue)
      }
      this.result = newResult
    },

    editTableLine: function (i) {
      const result = this.result[i]
      assert.notNull(result, 'Editing non-existent table line is impossible !')

      const color = result.color
      const value = result.value
      this.setEditValue(value, color, i)
      this.currentPickerColor = color
    },

    deleteTableLine: function (i) {
      this.result.splice(i, 1)
    },

    closeModalAndEmitResult: function () {
      this.show = false

      this.$emit(
        'save-color-ranges',
        {
          config: this.result,
          colorGradient: this.colorGradient,
          selectedPreset: this.hasPresets
            ? this.selectedPreset.key
            : null
        })
    }
  },

  components: {
    cleave,
    'sketch-picker': VueColor.Sketch,
    'sui-checkbox': suiCheckbox
  }

}

</script>
<style scoped>
.color-range-selector {
  .no-bottom-margin {
    margin-bottom: 0;
  }

  .table {
    margin-bottom: 0;

    input {
      width: 80px;
    }
    td {
      text-align: center;
    }
    th {
      text-align: center;
    }
  }

  .color-display {
    border-radius: 3px;
    padding-left: 65px;
    padding-bottom: 3px;
  }
}
</style>
